import React, { useState, useEffect } from 'react';
import { Phone, Mail, Heart, Star, Crown, ArrowUp, Award, Clock, Shield, Trophy } from 'lucide-react';

const Footer = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const services = [
    "Organizare completă botez",
    "Decorațiuni personalizate",
    "Catering & torturi speciale",
    "Fotografie & videografie",
    "Animație pentru copii",
    "Consultanță specializată"
  ];

  const quickLinks = [
    { name: "Acasă", id: "acasa" },
    { name: "Servicii", id: "servicii" },
    { name: "Despre Noi", id: "despre" },
    { name: "Contact", id: "contact" }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative navy-pattern text-white overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(245, 158, 11, 0.1) 0%, transparent 50%)`
          }}
        />
        
        {/* Floating particles */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full opacity-20 ${
              i % 3 === 0 ? 'bg-gold-300' : i % 3 === 1 ? 'bg-emerald-300' : 'bg-white'
            } ${i % 2 === 0 ? 'particle-1' : 'particle-2'}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${12 + Math.random() * 6}s`
            }}
          />
        ))}
      </div>

      <div className="relative">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-12">
            
            {/* Brand & Description */}
            <div className="md:col-span-1 fade-in-up">
              <div className="group cursor-pointer" onClick={scrollToTop}>
                <div className="flex items-center mb-6">
                  <Crown className="w-8 h-8 text-gold-400 mr-3 float" />
                  <h3 className="text-3xl font-bold gradient-gold font-display group-hover:scale-105 transition-transform duration-300">
                    BOTEZ DE POVESTE
                  </h3>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                Transformăm visurile în realitate, creând amintiri de neuitat pentru familia dumneavoastră. 
                Fiecare botez pe care îl organizăm este o poveste unică, plină de dragoste și bucurie.
              </p>
              
              <div className="flex items-center text-gold-400 mb-4 group">
                <Heart className="w-5 h-5 mr-3 group-hover:scale-110 group-hover:text-gold-300 transition-all duration-300" />
                <span className="group-hover:text-white transition-colors duration-300">
                  Făcut cu dragoste pentru familiile din România
                </span>
              </div>

              {/* Trust Badge */}
              {/* <div className="glass-navy rounded-xl p-4 border border-white/10">
                <div className="flex items-center text-sm text-gray-300">
                  <Shield className="w-4 h-4 mr-2 text-emerald-400" />
                  Firma înregistrată legal • CUI: 44057432
                </div>
              </div> */}
            </div>

            {/* Quick Links */}
            <div className="fade-in-up stagger-1">
              <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                <Crown className="w-5 h-5 mr-2 text-gold-400" />
                Navigație Rapidă
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button 
                      onClick={() => scrollToSection(link.id)}
                      className="text-gray-300 hover:text-gold-400 transition-all duration-300 flex items-center group"
                    >
                      <div className="w-2 h-2 bg-gold-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div className="fade-in-up stagger-2">
              <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                <Award className="w-5 h-5 mr-2 text-gold-400" />
                Serviciile Noastre
              </h4>
              <ul className="space-y-3 text-gray-300">
                {services.map((service, index) => (
                  <li 
                    key={index} 
                    className="hover:text-gold-400 transition-colors duration-300 cursor-pointer flex items-center group"
                  >
                    <div className="w-1.5 h-1.5 bg-gold-400 rounded-full mr-3 opacity-60 group-hover:opacity-100 group-hover:scale-150 transition-all duration-300"></div>
                    {service}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="fade-in-up stagger-3">
              <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                <Phone className="w-5 h-5 mr-2 text-gold-400" />
                Contact Direct
              </h4>
              
              <div className="space-y-4">
                {/* Phone */}
                <div className="group">
                  <a 
                    href="tel:+40740048888" 
                    className="flex items-center p-4 glass-navy rounded-xl border border-white/10 hover:border-gold-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-gold-500/20 hover-lift"
                  >
                    <Phone className="w-5 h-5 mr-3 text-gold-400 group-hover:scale-110 transition-transform duration-300" />
                    <div>
                      <div className="text-white font-semibold group-hover:text-gold-300 transition-colors duration-300">
                        +40-740-048 888
                      </div>
                      <div className="text-sm text-gray-400">Luni - Vineri: 09:00 - 18:00</div>
                    </div>
                  </a>
                </div>

                {/* Email */}
                <div className="group">
                  <a 
                    href="mailto:contact@trusoubotez.com" 
                    className="flex items-center p-4 glass-navy rounded-xl border border-white/10 hover:border-gold-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-gold-500/20 hover-lift"
                  >
                    <Mail className="w-5 h-5 mr-3 text-gold-400 group-hover:scale-110 transition-transform duration-300" />
                    <div>
                      <div className="text-white font-semibold group-hover:text-gold-300 transition-colors duration-300">
                        contact@trusoubotez.com
                      </div>
                      <div className="text-sm text-gray-400">Răspuns în maxim 24h</div>
                    </div>
                  </a>
                </div>

                {/* Program */}
                <div className="glass-navy rounded-xl border border-white/10 p-4">
                  
                </div>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="border-t border-gray-700 pt-12 mt-12 fade-in-up">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { number: "150+", label: "Botezuri organizate", icon: Crown },
                { number: "98%", label: "Clienți mulțumiți", icon: Heart },
                { number: "5★", label: "Rating mediu", icon: Star },
                { number: "24h", label: "Timp răspuns", icon: Trophy }
              ].map((stat, index) => (
                <div 
                  key={index} 
                  className="group hover:scale-105 transition-transform duration-300"
                >
                  <div className="glass-navy rounded-2xl p-6 border border-white/10 hover:border-gold-400/30 transition-all duration-300">
                    <div className={`w-16 h-16 rounded-2xl mx-auto mb-3 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg ${
                      index === 0 ? 'bg-gradient-to-r from-navy-600 to-navy-700' :
                      index === 1 ? 'bg-gradient-to-r from-emerald-600 to-emerald-700' :
                      index === 2 ? 'bg-gradient-to-r from-gold-600 to-gold-700' :
                      'bg-gradient-to-r from-gold-700 to-navy-700'
                    }`}>
                      <stat.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2 group-hover:text-gold-300 transition-colors duration-300">
                      {stat.number}
                    </div>
                    <div className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors duration-300">
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-700 bg-black/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              
              <div className="text-center md:text-left mb-6 md:mb-0">
                <p className="text-gray-400 mb-2 flex items-center justify-center md:justify-start">
                  <Crown className="w-4 h-4 mr-2 text-gold-400" />
                  © {currentYear} BOTEZ DE POVESTE. Toate drepturile rezervate.
                </p>
                <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Shield className="w-3 h-3 mr-1 text-emerald-400" />
                    {/* CUI: 44057432 */}
                  </div>
                  <span className="hidden md:inline">|</span>
                  <div className="flex items-center">
                    <Award className="w-3 h-3 mr-1 text-gold-400" />
                    Organizare evenimente de botez în România
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-6">
                <div className="text-sm text-gray-400 hidden md:block">
                  Website realizat cu dragoste pentru familiile din România
                </div>
                <span className="text-gray-600 hidden md:inline">|</span>
                <button 
                  onClick={scrollToTop}
                  className="glass-navy rounded-full p-3 border border-white/10 hover:border-gold-400/50 transition-all duration-300 group hover-lift"
                >
                  <ArrowUp className="w-5 h-5 text-gray-400 group-hover:text-gold-400 transition-colors duration-300" />
                </button>
              </div>
            </div>
          </div>
        </div>

        
      </div>

      {/* Decorative Bottom Wave */}
      <div className="absolute bottom-0 left-0 w-full h-2 bg-gradient-to-r from-navy-600 via-gold-600 to-emerald-600"></div>
    </footer>
  );
};

export default Footer;